
import * as React from "react";

// Define the Toast type
export type Toast = {
  id: string;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  variant?: "default" | "destructive";
};

// Create context for managing toasts
type ToastContextType = {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
};

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

// Create the provider component
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      setToasts((prevToasts) => [...prevToasts, { id, ...toast }]);

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        removeToast(id);
      }, 5000);
    },
    []
  );

  const removeToast = React.useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const contextValue = React.useMemo(() => {
    return { toasts, addToast, removeToast };
  }, [toasts, addToast, removeToast]);

  return <ToastContext.Provider value={contextValue}>{children}</ToastContext.Provider>;
}

// Custom hook to use the toast context
export function useToast() {
  const context = React.useContext(ToastContext);
  
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  
  const toast = (props: Omit<Toast, "id">) => {
    context.addToast(props);
  };
  
  return {
    ...context,
    toast,
  };
}

// Function for use outside of React components
export const toast = (props: Omit<Toast, "id">) => {
  // This is a placeholder that will be overwritten by the actual implementation
  console.warn("toast() was called outside of a component. Use within a component with useToast() hook instead.");
};
